import React, { useState, useMemo } from 'react';
import NiceAvatar, { genConfig } from 'react-nice-avatar';
import { DirectAnimalAvatar, DirectAvatarConfig } from '../../utils/DirectAnimalAvatar';
import { PetAvatarConfig } from '../../types/user';
import secureLogger from '../../utils/secureLogger';

interface AvatarProps {
  // Nueva interfaz
  user?: {
    type: 'human' | 'pet';
    name: string;
    petAvatarConfig?: PetAvatarConfig;
    avatarUrl?: string;
    avatarCustomization?: any; // Para NiceAvatar
    userId?: string; // ID único para consistencia
    email?: string; // Email para identificación adicional
  };
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  
  // Interfaz de compatibilidad (para casos que no se han actualizado)
  seed?: string;
  customization?: any;
  alt?: string;
  isPet?: boolean;
  petAvatarConfig?: PetAvatarConfig;
  src?: string;
  userIdentifier?: string; // ID único para generar seeds consistentes
}

const Avatar: React.FC<AvatarProps> = ({ 
  user, 
  size = 'md', 
  className = '',
  // Props de compatibilidad
  seed,
  customization,
  alt,
  isPet,
  petAvatarConfig,
  src,
  userIdentifier
}) => {
  const [imageError, setImageError] = useState(false);

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-20 h-20'
  };

  const sizePixels = {
    sm: 32,
    md: 48,
    lg: 64,
    xl: 80
  };

  // Estilos inline como fallback para asegurar visibilidad
  const inlineSizeStyles = {
    sm: { width: '32px', height: '32px' },
    md: { width: '48px', height: '48px' },
    lg: { width: '64px', height: '64px' },
    xl: { width: '80px', height: '80px' }
  };

  const baseClasses = `${sizeClasses[size]} rounded-full border-2 border-gray-200 bg-white overflow-hidden ${className}`;

  // Si no tenemos user pero tenemos props de compatibilidad, crear user object
  let actualUser = user;
  if (!user && (seed || alt || src || userIdentifier)) {
    actualUser = {
      type: isPet ? 'pet' : 'human',
      name: alt || seed || 'Usuario',
      avatarUrl: src,
      petAvatarConfig: petAvatarConfig,
      avatarCustomization: customization,
      userId: userIdentifier || seed || alt
    };
  }

  // Si aún no tenemos user, crear uno por defecto
  if (!actualUser) {
    actualUser = {
      type: 'human',
      name: 'Usuario',
      userId: 'default-user'
    };
  }

  // Generar un identificador único y consistente para este usuario
  const uniqueIdentifier = useMemo(() => {
    const id = actualUser?.userId || 
               actualUser?.email || 
               userIdentifier || 
               seed || 
               actualUser?.name || 
               'default';
    return id;
  }, [actualUser, userIdentifier, seed]);

  // Generar configuración del avatar humano con NiceAvatar
  const avatarConfig = useMemo(() => {
    if (actualUser?.type === 'human') {
      // Solo usar avatarCustomization si tiene propiedades válidas (no es un objeto vacío)
      const hasValidCustomization = actualUser.avatarCustomization && 
                                    typeof actualUser.avatarCustomization === 'object' &&
                                    Object.keys(actualUser.avatarCustomization).length > 0;
      
      if (hasValidCustomization) {
        return actualUser.avatarCustomization;
      }
      
      // Fallback determinista basado en el identificador único
      const config = genConfig(uniqueIdentifier);
      return config;
    }
    return null;
  }, [actualUser, uniqueIdentifier]);

  // Para avatares humanos
  if (actualUser.type === 'human') {
    // Si tiene URL de imagen personalizada
    if (actualUser.avatarUrl && !imageError) {
      return (
        <img 
          src={actualUser.avatarUrl} 
          alt={actualUser.name}
          className={baseClasses}
          style={inlineSizeStyles[size]}
          onError={() => {
            setImageError(true);
          }}
        />
      );
    }
    
    // Usar NiceAvatar para avatares ilustrados hermosos
    return (
      <div 
        className={baseClasses}
        style={inlineSizeStyles[size]}
        title={`Avatar de ${actualUser.name} (${uniqueIdentifier})`}
      >
        <NiceAvatar
          style={{ width: '100%', height: '100%' }}
          {...avatarConfig}
        />
      </div>
    );
  }

  // Para avatares de mascota
  if (actualUser.type === 'pet') {
    try {
      const avatarSvg = actualUser.petAvatarConfig 
        ? DirectAnimalAvatar.generateSVG({
            ...actualUser.petAvatarConfig,
            isRound: true,
            size: sizePixels[size]
          } as DirectAvatarConfig)
        : DirectAnimalAvatar.generateFromSeed(uniqueIdentifier, { 
            size: sizePixels[size], 
            isRound: true 
          });

      return (
        <div 
          className={`${baseClasses} flex items-center justify-center`}
          style={{
            ...inlineSizeStyles[size],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title={`Avatar de ${actualUser.name} (mascota)`}
          dangerouslySetInnerHTML={{ __html: avatarSvg }}
        />
      );
    } catch (error) {
      secureLogger.error(`Error rendering pet avatar for: ${actualUser.name}`, error);
      // Fallback para mascotas
      return (
        <div 
          className={baseClasses}
          style={{
            ...inlineSizeStyles[size],
            borderRadius: '50%',
            border: '2px solid #d1d5db',
            background: 'linear-gradient(135deg, #fb923c 0%, #ef4444 100%)',
            color: 'white',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title={`Avatar de ${actualUser.name} (mascota - fallback)`}
        >
          <span style={{ fontSize: size === 'sm' ? '16px' : '24px' }}>🐾</span>
        </div>
      );
    }
  }

  // Fallback genérico
  return (
    <div 
      className={baseClasses}
      style={{
        ...inlineSizeStyles[size],
        borderRadius: '50%',
        border: '2px solid #d1d5db',
        background: '#d1d5db',
        color: '#6b7280',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      title={`Avatar genérico de ${actualUser.name}`}
    >
      <span style={{ fontSize: '18px' }}>👤</span>
    </div>
  );
};

export default Avatar; 